import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react'
import {
  APILoadingStatus,
  AdvancedMarker,
  ControlPosition,
  Map,
  MapControl,
  Pin,
  useApiLoadingStatus,
  useMap,
  useMapsLibrary,
} from '@vis.gl/react-google-maps'
import { Crosshair } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_MAP_ID,
  RIYADH_CENTER,
  useGoogleMapsAuthFailed,
} from '@/lib/googleMaps'
import { GoogleMapsProvider } from '@/components/maps/GoogleMapsProvider'
import type { LatLng } from '@/utils/geo'

/**
 * Zoom used when every point sits in the same place. Tracking opens once the provider
 * has arrived, so provider and destination coinciding is the *normal* case, not an edge
 * case — left to `fitBounds`, identical points slam the map to maximum zoom.
 */
const SINGLE_POINT_ZOOM = 16
/** Ceiling applied after a fit, for two points a few metres apart. */
const MAX_FIT_ZOOM = 17
const DEFAULT_ZOOM = 13
const FIT_PADDING = { top: 56, right: 56, bottom: 56, left: 56 }
/** Two points closer than this are treated as one. */
const SAME_POINT_EPSILON = 1e-5

// One light palette: this dashboard has no dark mode (`globals.css` defines no `.dark`).
const COLORS = {
  connector: '#334155',
  providerFresh: '#16a34a',
  providerStale: '#6b7280',
  /** The ring is what separates a pin from the tile beneath it. */
  ring: '#ffffff',
  destination: '#131375',
  destinationGlyph: '#ffffff',
} as const

export interface LiveTrackingMapLabels {
  /** Shown when the Maps script failed to load — this one is retryable. */
  loadFailed: string
  /** Shown when Google rejected the key — settled for the page's life, no retry. */
  authFailed: string
  retry: string
  fit: string
}

export interface LiveTrackingMapProps {
  /** Last reported provider position, or `null` when none has arrived yet. */
  provider: LatLng | null
  /** Where the visit is taking place, when it can be resolved. */
  destination: LatLng | null
  headingDegrees?: number | null
  accuracyMeters?: number | null
  /** Position is older than the backend's freshness window. */
  isStale?: boolean
  /** Rendered instead of the map whenever the map cannot be shown. */
  fallback: ReactNode
  labels: LiveTrackingMapLabels
  className?: string
}

function samePoint(a: LatLng, b: LatLng): boolean {
  return Math.abs(a.lat - b.lat) < SAME_POINT_EPSILON && Math.abs(a.lng - b.lng) < SAME_POINT_EPSILON
}

/** 15° buckets, so a fractional heading change does not rebuild the marker on every poll. */
function quantiseHeading(heading: number | null | undefined): number | null {
  if (heading == null || !Number.isFinite(heading)) return null
  return Math.round(((heading % 360) + 360) % 360 / 15) * 15
}

// ─── Overlays drawn imperatively ─────────────────────────────────────────────

interface OverlaysProps {
  provider: LatLng | null
  destination: LatLng | null
  accuracyMeters: number | null
  isStale: boolean
}

/**
 * The dashed provider→destination connector and the accuracy circle.
 *
 * The dashes are repeated symbols on a polyline drawn at `strokeOpacity: 0`, so the
 * colour has to live on the *symbol* — `strokeColor` on the polyline itself is never
 * painted, and with no explicit colour Google defaults the symbol to black.
 */
function Overlays({ provider, destination, accuracyMeters, isStale }: OverlaysProps) {
  const map = useMap()
  const mapsLib = useMapsLibrary('maps')

  const canConnect =
    !!provider && !!destination && !samePoint(provider, destination)

  useEffect(() => {
    if (!map || !mapsLib || !canConnect || !provider || !destination) return

    const line = new mapsLib.Polyline({
      map,
      path: [provider, destination],
      strokeOpacity: 0,
      icons: [
        {
          icon: {
            path: 'M 0,-1 0,1',
            strokeOpacity: 0.9,
            strokeColor: COLORS.connector,
            strokeWeight: 2,
            scale: 3,
          },
          offset: '0',
          repeat: '14px',
        },
      ],
    })

    return () => line.setMap(null)
  }, [map, mapsLib, canConnect, provider, destination])

  useEffect(() => {
    if (!map || !mapsLib || !provider || !accuracyMeters || accuracyMeters <= 0) return

    const color = isStale ? COLORS.providerStale : COLORS.providerFresh
    const circle = new mapsLib.Circle({
      map,
      center: provider,
      radius: accuracyMeters,
      fillColor: color,
      fillOpacity: isStale ? 0.04 : 0.08,
      strokeColor: color,
      strokeOpacity: isStale ? 0.15 : 0.25,
      strokeWeight: 1,
      clickable: false,
    })

    return () => circle.setMap(null)
  }, [map, mapsLib, provider, accuracyMeters, isStale])

  return null
}

// ─── Provider marker ─────────────────────────────────────────────────────────

function ProviderMarkerContent({ heading, isStale }: { heading: number | null; isStale: boolean }) {
  const color = isStale ? COLORS.providerStale : COLORS.providerFresh

  return (
    <div
      className="flex h-8 w-8 items-center justify-center rounded-full shadow-md"
      style={{ backgroundColor: color, border: `2px solid ${COLORS.ring}` }}
    >
      {heading == null ? (
        <span className="block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: COLORS.ring }} />
      ) : (
        <svg
          viewBox="0 0 16 16"
          className="h-4 w-4"
          style={{ transform: `rotate(${heading}deg)` }}
          aria-hidden="true"
        >
          <path d="M8 1.5 13 14 8 11 3 14z" fill={COLORS.ring} />
        </svg>
      )}
    </div>
  )
}

// ─── Inner map (inside the API context) ──────────────────────────────────────

function LiveTrackingMapInner({
  provider,
  destination,
  headingDegrees,
  accuracyMeters = null,
  isStale = false,
  fallback,
  labels,
  className,
}: LiveTrackingMapProps) {
  const status = useApiLoadingStatus()
  const authFailed = useGoogleMapsAuthFailed()
  const map = useMap()

  // Refs, not state, on purpose: flipping any of these must not re-render, because a
  // re-render re-runs the fit effect — the exact loop this prevents. `onDragstart` also
  // fires mid-gesture, and a setState there renders during a Google gesture.
  const fittedCountRef = useRef(0)
  const userMovedRef = useRef(false)
  const programmaticRef = useRef(false)
  const clampPendingRef = useRef(false)

  const heading = quantiseHeading(headingDegrees)

  const points = useMemo(
    () => [provider, destination].filter((p): p is LatLng => p != null),
    [provider, destination]
  )

  const fitToPoints = useCallback(() => {
    if (!map || points.length === 0) return

    programmaticRef.current = true

    if (points.length === 1 || samePoint(points[0], points[1])) {
      map.setCenter(points[0])
      map.setZoom(SINGLE_POINT_ZOOM)
      return
    }

    const bounds = new google.maps.LatLngBounds()
    points.forEach((point) => bounds.extend(point))
    clampPendingRef.current = true
    map.fitBounds(bounds, FIT_PADDING)
  }, [map, points])

  // Fit once when the first point arrives, and once more when the second one does — so a
  // card that mounts knowing only the address still frames both pins when the provider
  // reports. Never on a mere position update, and never once the user has moved the map.
  useEffect(() => {
    if (!map || userMovedRef.current) return
    if (points.length <= fittedCountRef.current) return
    fittedCountRef.current = points.length
    fitToPoints()
  }, [map, points, fitToPoints])

  const handleUserMove = useCallback(() => {
    if (programmaticRef.current) return
    userMovedRef.current = true
  }, [])

  const handleIdle = useCallback(() => {
    if (clampPendingRef.current && map) {
      clampPendingRef.current = false
      const zoom = map.getZoom()
      // Two points a few metres apart otherwise fit to street level.
      if (zoom != null && zoom > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM)
    }
    programmaticRef.current = false
  }, [map])

  const handleFitClick = useCallback(() => {
    userMovedRef.current = false
    fitToPoints()
  }, [fitToPoints])

  if (authFailed) {
    // Auth is settled for the page's lifetime — offering a retry would only mislead.
    return <MapFallback message={labels.authFailed}>{fallback}</MapFallback>
  }

  if (status === APILoadingStatus.FAILED) {
    return (
      <MapFallback
        message={labels.loadFailed}
        onRetry={() => window.location.reload()}
        retryLabel={labels.retry}
      >
        {fallback}
      </MapFallback>
    )
  }

  if (status !== APILoadingStatus.LOADED) {
    return <Skeleton className={cn('h-[320px] w-full rounded-lg', className)} />
  }

  return (
    // Forced LTR: `LocalizationContext` sets `document.documentElement.dir = 'rtl'` for
    // Arabic, and Google's `.gm-style` internals are positioned from `left` and inherit
    // `direction` — inside the RTL shell the controls and overlays are mispositioned.
    <div dir="ltr" className={cn('h-[320px] w-full overflow-hidden rounded-lg border border-gray-200', className)}>
      <Map
        mapId={GOOGLE_MAPS_MAP_ID}
        defaultCenter={points[0] ?? RIYADH_CENTER}
        defaultZoom={DEFAULT_ZOOM}
        gestureHandling="cooperative"
        disableDefaultUI={false}
        mapTypeControl={false}
        streetViewControl={false}
        fullscreenControl={false}
        onDragstart={handleUserMove}
        onZoomChanged={handleUserMove}
        onIdle={handleIdle}
      >
        <Overlays
          provider={provider}
          destination={destination}
          accuracyMeters={accuracyMeters}
          isStale={isStale}
        />

        {destination && (
          <AdvancedMarker position={destination}>
            <Pin
              background={COLORS.destination}
              borderColor={COLORS.ring}
              glyphColor={COLORS.destinationGlyph}
            />
          </AdvancedMarker>
        )}

        {provider && (
          <AdvancedMarker position={provider} zIndex={2}>
            <ProviderMarkerContent heading={heading} isStale={isStale} />
          </AdvancedMarker>
        )}

        {/* Top-end: Google owns RIGHT_BOTTOM (zoom) and LEFT_BOTTOM (the mandatory logo).
            Overlapping the zoom buttons makes them unclickable; covering the logo breaks
            the Maps terms. */}
        <MapControl position={ControlPosition.RIGHT_TOP}>
          <button
            type="button"
            onClick={handleFitClick}
            title={labels.fit}
            aria-label={labels.fit}
            className="m-2.5 flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
          >
            <Crosshair className="h-4 w-4" />
          </button>
        </MapControl>
      </Map>
    </div>
  )
}

// ─── Fallback chrome ─────────────────────────────────────────────────────────

function MapFallback({
  message,
  onRetry,
  retryLabel,
  children,
}: {
  message: string
  onRetry?: () => void
  retryLabel?: string
  children: ReactNode
}) {
  return (
    <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/60 p-5">
      {children}
      <p className="mt-3 text-xs text-gray-500">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 text-xs font-medium text-brand-primary hover:underline"
        >
          {retryLabel}
        </button>
      )}
    </div>
  )
}

/**
 * Live-tracking map: a provider pin, the visit's destination, and a dashed connector.
 *
 * Owns loader gating and viewport policy so no call site has to. It is presentational —
 * it receives resolved numbers and never fetches anything itself.
 */
export function LiveTrackingMap(props: LiveTrackingMapProps) {
  // No key in this build (common locally). Never mount `<Map>` — it would render a grey
  // slab that is indistinguishable from a rejected key.
  if (!GOOGLE_MAPS_API_KEY) {
    return <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/60 p-5">{props.fallback}</div>
  }

  return (
    <GoogleMapsProvider>
      <LiveTrackingMapInner {...props} />
    </GoogleMapsProvider>
  )
}
